import { PotentialTrip, PotentialTripKindEnum, PotentialTripTypeNameEnum } from '@/models/gen/graphql';
import { clearFormStorage, getFormStorage, setFormStorage } from '@/common/Form';

import { Schema } from '@/utils';

type LabelLocale =
  | 'Sch Type'
  | 'Client'
  | 'Airline'
  | 'Airport'
  | 'Sch Time'
  | 'Arrival Date / Time'
  | 'Arrival / Departure'
  | 'Plt'
  | 'F/A'
  | 'P/U'
  | 'D/O'
  | 'Pickup Date'
  | 'Act Time'
  | 'Flight # / Loop Name'
  | 'Drop City'
  | 'Split'
  | 'Include This Trip In Future Manifests?'
  | 'Yes'
  | 'No'
  | 'Select Location'
  // Optional, but required for UPS
  | 'Crew Id'
  | 'First Name'
  | 'Last Name';

type ValidationLocale = 'Required';

type ActionLocale = 'Save' | 'Save As Copy' | 'Cancel';

export namespace PotentialTripFormDefinitions {
  export type Locale = LabelLocale | ValidationLocale | ActionLocale;
  export type Value = Partial<PotentialTrip> & {
    dropCity?: string;
    scheduledDate?: string[];
    scheduledTime?: string;
    flightNumberOrLoopName?: string;
    firstName?: string;
    lastName?: string;
    crewId?: number;
    providerId?: string;
    importId?: string;
  };
  export type Props = {
    onSubmit?: (data: Value) => void | Value | Promise<Value> | undefined;
    onClose?: () => void;
    onRefresh?: () => void;
    locale?: Partial<Record<Locale, string>>;
    value?: Value;
  };
  export const initValue: Value = {
    typeName: undefined,
    payerProviderId: undefined,
    airportCode: undefined,
    servicerIataAirlineCode: undefined,
    scheduledDate: undefined,
    scheduledTime: undefined,
    kind: undefined,
    flightNumberOrLoopName: '',
    flightNumber: undefined,
    loopName: undefined,
    dropCity: undefined,
    pilots: 0,
    attendants: 0,
    firstName: '',
    lastName: '',
    crewId: undefined,
    puLocation: undefined,
    doLocation: undefined,
    fromManifest: 0,
  };
  export const validator = (locale: (original: Locale) => string) =>
    Schema((z) => z.intersection(pilotAndAttendantsSchema(locale), potentialTripsSchema(locale)));

  const potentialTripsSchema = (locale: (original: Locale) => string) =>
    Schema((z) =>
      z.object({
        typeName: z.nativeEnum(PotentialTripTypeNameEnum, { message: locale('Required') }),
        payerProviderId: z.string().min(1, { message: locale('Required') }),
        airportCode: z.string().min(3, { message: locale('Required') }),
        servicerIataAirlineCode: z.string().min(1, { message: locale('Required') }),
        scheduledDate: z
          .array(z.string().min(1))
          .min(1, { message: locale('Required') })
          .refine((val) => val.every((date) => date && date.length > 0), {
            message: locale('Required'),
          }),
        kind: z.nativeEnum(PotentialTripKindEnum, { message: locale('Required') }),
        flightNumberOrLoopName: z.string().min(1, { message: locale('Required') }),
        firstName: z.string().min(1, { message: locale('Required') }),
        lastName: z.string().min(1, { message: locale('Required') }),
        crewId: z.number(),
        puLocation: z.string().min(1, { message: locale('Required') }),
        doLocation: z.string().min(1, { message: locale('Required') }),
        scheduledTime: z.string().min(1, { message: locale('Required') }),
      })
    );
  const pilotAndAttendantsSchema = (locale: (original: Locale) => string) =>
    Schema((z) =>
      z
        .object({
          pilots: z.number(),
          attendants: z.number(),
        })
        .refine((data) => data.pilots > 0 || data.attendants > 0, {
          message: locale('Required'),
          path: ['pilots'],
        })
        .refine((data) => data.pilots > 0 || data.attendants > 0, {
          message: locale('Required'),
          path: ['attendants'],
        })
    );
  export const saveForm = (values: Value): void => setFormStorage('PotentialTripForm', values);
  export const loadForm = (): Value => getFormStorage('PotentialTripForm');
  export const clearForm = (): void => clearFormStorage('PotentialTripForm');
}
